import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import {
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom/cjs/react-router-dom.min';
import styled from 'styled-components';
import { Button } from 'components/Button';
import Container from 'components/Container';
import ContainerHeader from 'components/ContainerHeader';
import { Input } from 'components/Form/Input';
import Spacer from 'components/Spacer';
import Text from 'components/Text';
import { PointOfSaleSettings } from 'pages/app/Accounts/AddAccounts/components/PointOfSaleSettings';
import { useGetEvent } from 'pages/app/Events/EventsMenu/gql/queries/get-event';
import { errorMessage, successMessage } from 'shared/alerts';
import { useCart } from 'shared/CartContext';
import { validateIpAddress } from 'shared/utils/ValidateIp';
import { useUpdatePOSSettings } from './gql/mutation/useUpdatePosSettings';
import { useGetAccountPosSettings } from './gql/queries/useGetAccountPosSettings';
import { ReaderSelection } from './ReaderSelection';

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;

  @media (max-width: 860px) {
    height: auto;
    padding: 20px;
    flex-direction: column;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-inline: 15px;
  width: 33.3333%;

  @media (max-width: 860px) {
    width: 100%;
    padding: 0;
  }
`;

const formValues = ['pos_settings'];

export const PosSettings = () => {
  const { id } = useParams();

  const { accountId } = useCart();

  const history = useHistory();

  const location = useLocation();
  const { event } = location.state || {};

  const [ipAddress, setIpAddress] = useState('');
  const [isValidIp, setIsValidIp] = useState(true);

  const [selectedReader, setSelectedReader] = useState(null);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setIpAddress(value);

    // Validate IP address
    setIsValidIp(validateIpAddress(value));
  };

  const handleChangeReader = (reader) => {
    setSelectedReader(reader);
  };

  const updatePOSSettings = useUpdatePOSSettings();

  const { data } = useGetEvent(parseInt(id), !!event);

  const currentEvent = event ?? data?.getEvent;

  const { data: accountData } = useGetAccountPosSettings(
    currentEvent?.account_id,
    !!currentEvent.pos_settings
  );

  const accountPosSettings = accountData?.getAccount;

  function getInitialValues(event, keys) {
    const blankValues = {
      ...keys.reduce((values, key) => ({ ...values, [key]: '' }), {}),
    };

    const initialValues = !event
      ? blankValues
      : {
          ...keys.reduce((formValues, key) => {
            return event[key]
              ? {
                  ...formValues,
                  [key]: event[key],
                }
              : formValues;
          }, {}),
        };

    return initialValues;
  }

  function getPOSSettingsInput({ data }) {
    const input = {
      ...data,
    };
    return input;
  }

  const UpdateIpAddress = async () => {
    // Save the input IP address to local storage if valid
    if (isValidIp) {
      localStorage.setItem('readerIp', ipAddress);
    }
  };

  const SaveReader = async () => {
    // Save the selected reader id to cookies with an expiration time of 12 hours
    document.cookie = `${
      accountId ? accountId : currentEvent?.account_id
    }-reader=${selectedReader?.value.reader_id}; expires=${new Date(
      new Date().getTime() + 12 * 60 * 60 * 1000
    ).toUTCString()}; path=/`;
  };

  const pathname = useLocation().pathname;
  const userType =
    ['admin-employee', 'admin-track'].find((type) => pathname.includes(type)) ||
    'admin';

  const openCashDrawerReport = () =>
    history.push(`/${userType}/events/cashDrawerReport/${event.id}`, {
      event: event,
    });

  // Check if the IP address is saved in local storage
  useEffect(() => {
    const savedIpAddress = localStorage.getItem('readerIp');
    if (savedIpAddress) {
      setIpAddress(savedIpAddress);
    }
  }, []);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={getInitialValues(
        currentEvent ?? accountPosSettings,
        formValues
      )}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (values, { setSubmitting, setErrors, resetForm }) => {
        setSubmitting(true);

        const data = getPOSSettingsInput({
          data: values,
        });

        formValues.forEach((key) => {
          if (data[key] === '' || data[key] === undefined) delete data[key];
        });

        if (data['pos_settings']) {
          delete data[`pos_settings`]['__typename'];
        }

        const response = await updatePOSSettings({
          event_id: +id,
          pos_settings: data.pos_settings,
        });

        if (!response || response.errors) {
          errorMessage(response);
          setSubmitting(false);
          return setErrors(response.errors);
        } else {
          // Update IP address value
          UpdateIpAddress();

          // Save selected reader
          if (selectedReader) {
            SaveReader();
          }

          successMessage('POS Settings Updated');
          setSubmitting(false);
          resetForm();
          history.goBack();
        }

        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
      }) => (
        <Container>
          <form onSubmit={handleSubmit}></form>

          <ContainerHeader borderWidth={'1px'} style={{ paddingRight: '15px' }}>
            <Text
              type="heading"
              color="#3C4144"
              inlineStyle={{ marginRight: 25, whiteSpace: 'nowrap' }}
            >
              POS Settings
            </Text>

            <ButtonContainer>
              <Button
                onClick={openCashDrawerReport}
                disabled={isSubmitting}
                style={{ minWidth: 150 }}
                outlined={true}
              >
                My Box Office Report
              </Button>

              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                style={{ minWidth: 150 }}
              >
                Save
              </Button>
            </ButtonContainer>
          </ContainerHeader>

          <div style={{ padding: 20 }}>
            <PointOfSaleSettings
              handleChange={(target) => {
                const { name, value } = target.target;

                const newSettings = {
                  ...values.pos_settings,
                  [name]: value,
                };

                typeof name === 'string' &&
                  handleChange({
                    target: {
                      name: 'pos_settings',
                      value: newSettings,
                    },
                  });
              }}
              mainValues={values}
              handleBlur={handleBlur}
              errors={errors}
              touched={touched}
            />

            <Spacer size={20} />

            <InputContainer>
              <Input
                id="ip-address"
                name="ipAddress"
                label="IP Address"
                placeholder="xxx.xxx.xxx.xxx"
                required
                onChange={handleInputChange}
                value={ipAddress}
                error={
                  !isValidIp && ipAddress !== '' ? 'Invalid IP address' : ''
                }
              />

              <ReaderSelection
                asModal={false}
                handleChangeReader={handleChangeReader}
                account_id={currentEvent?.account_id}
              />
            </InputContainer>
          </div>
        </Container>
      )}
    </Formik>
  );
};
